/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

type Translate = (key: string, values?: Record<string, number | string>) => string;

export interface SensorRequestMessagePart {
    isSensor: boolean;
    value: string;
}

const SENSOR_URN_PATTERN = /(?:calc\.(?:[A-Za-z0-9_]+\.)*)?(?:GN|Mue)\.[A-Za-z0-9_§()/-]+(?:\.[A-Za-z0-9_§()/-]+)*/g;

export function splitSensorRequestMessage(message: string): SensorRequestMessagePart[] {
    const parts: SensorRequestMessagePart[] = [];
    let previousIndex = 0;

    for (const match of message.matchAll(SENSOR_URN_PATTERN)) {
        const [sensor] = match;
        const matchIndex = match.index ?? 0;

        if (matchIndex > previousIndex) {
            parts.push({
                isSensor: false,
                value: message.slice(previousIndex, matchIndex),
            });
        }

        parts.push({ isSensor: true, value: sensor });
        previousIndex = matchIndex + sensor.length;
    }

    if (previousIndex < message.length || parts.length === 0) {
        parts.push({
            isSensor: false,
            value: message.slice(previousIndex),
        });
    }

    return parts;
}

export function getCurrentValueSensorNotFoundMessage(
    t: Translate,
    sensor: string,
): string {
    return t('Sensor {sensor} was not found.', { sensor });
}

export function getCurrentValueSensorNotNumericMessage(
    t: Translate,
    sensor: string,
): string {
    return t('Sensor {sensor} returned no numeric current value.', { sensor });
}

export function getIntervalSensorsNotFoundMessage(
    t: Translate,
    sensors: string[],
): string {
    return t('No interval values were found for sensors {sensors}.', {
        sensors: sensors.join(', '),
    });
}

export function getTimeSeriesSensorNotFoundMessage(
    t: Translate,
    sensor: string,
): string {
    return t('No time series values were found for sensor {sensor}.', { sensor });
}

export function getTimeSeriesSensorNotNumericMessage(
    t: Translate,
    sensor: string,
): string {
    return t('Time series values for sensor {sensor} are not numeric.', { sensor });
}
